import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInjection } from 'inversify-react';
import { TICKETS_MODULE_IDS } from '../../../lib/widget/tickets/tickets.types';
import { ITicketsController } from '../../../lib/widget/tickets/tickets.interface';
import { getPoolTicketsIdsSet } from '../../../helpers/tickets';
import { ITicket } from '../../../types/ticket';
import CloseIcon from '../Icons/Close';
import TicketItem from './TicketItem';
import Styled from './styles';
import { TICKETS_POPOVER_EVENTS } from '../../../constants/CONTROLLERS_EVENT_TYPES';

interface Props {
  isHidden?: boolean;
}

const TicketsModal: React.FC<Props> = ({ isHidden }) => {
  const { t } = useTranslation();
  const ticketsController = useInjection<ITicketsController>(TICKETS_MODULE_IDS.TICKETS_CONTROLLER);

  const [state, setState] = useState({
    isHidden: true,
    data: null,
    standData: null,
  });

  useEffect(() => {
    const handleUpdate = ({ isHidden, data, standData }) => {
      setState({ isHidden, data, standData });
    };

    window.Widget.ticketsPopoverController.addListener(TICKETS_POPOVER_EVENTS.UPDATE, handleUpdate);

    return () => {
      window.Widget.ticketsPopoverController.removeListener(
        TICKETS_POPOVER_EVENTS.UPDATE,
        handleUpdate,
      );
    };
  }, []);

  const close = () => {
    window.Widget.ticketsPopoverController.hide();
  };

  const preventClose = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const addTicket = (ticket: ITicket) => {
    const { data } = state;

    if (!ticketsController.selectedTicketsIds.has(data.id)) {
      ticketsController.addId(data.id);
    }

    ticketsController.addPoolTicket(ticket);
  };

  const removeTicket = (ticket: ITicket) => {
    ticketsController.removePoolTicket(ticket);

    const { data } = state;
    const selectedTicketsIdsSet = getPoolTicketsIdsSet(ticketsController.selectedTickets);
    const hasSelectedSeat = data.meta.tickets.some((t) => selectedTicketsIdsSet.has(t.ticket_id));

    if (!hasSelectedSeat) {
      ticketsController.removeId(data.id);
    }
  };

  const selectStandTicket = (sectorId: string, ticket: ITicket) => {
    ticketsController.showTicketsCountModal(sectorId, ticket);
  };

  const renderData = (data) => {
    const rowLabel = t('rowNumber', { number: data.row });
    const seatLabel = t('seatNumber', { number: data.seat });
    return (
      <div>
        <Styled.Title>
          {t('sector')} {data.sector}, {rowLabel}, {seatLabel}
        </Styled.Title>
        <Styled.Help>{t('selectSeller')}</Styled.Help>
        <Styled.List>
          {data.meta.tickets.map((item) => (
            <TicketItem
              key={item.ticket_id}
              addTicket={addTicket}
              removeTicket={removeTicket}
              data={item}
            />
          ))}
        </Styled.List>
      </div>
    );
  };

  const renderStandData = (data) => {
    return (
      <div>
        <Styled.Title>{data.label}</Styled.Title>
        <Styled.Help>{t('selectSeller')}</Styled.Help>
        <Styled.List>
          {data.meta.tickets.map((item) => (
            <TicketItem
              key={item.ticket_id}
              data={item}
              sector={data}
              selectTicket={selectStandTicket}
            />
          ))}
        </Styled.List>
      </div>
    );
  };

  if (state.isHidden || isHidden) return null;

  return (
    <Styled.Overlay onClick={close}>
      <Styled.Container onClick={preventClose}>
        <Styled.CloseButton type="button" onClick={close}>
          <CloseIcon />
        </Styled.CloseButton>
        {state.standData ? renderStandData(state.standData) : renderData(state.data)}
      </Styled.Container>
    </Styled.Overlay>
  );
};

export default TicketsModal;
